/** Assistance and attempts survive reloads and are shared by both markers. */
export interface ItemHelp {
  readonly hints: number;
  readonly answer: boolean;
  readonly shown?: boolean;
  readonly listened?: boolean;
  readonly transcript?: boolean;
  readonly audioFallback?: boolean;
}

export type HelpAction =
  | "hint"
  | "answer"
  | "listened"
  | "transcript"
  | "audioFallback";

/** One answer and the assistance seen before it was submitted. */
export interface PracticeAttempt extends ItemHelp {
  readonly item: number;
  readonly passed: boolean;
  readonly outcome:
    | "first"
    | "hint"
    | "answer"
    | "retry"
    | "missed"
    | "transcript"
    | "text";
  readonly answerId?: string;
  readonly status?: string;
}

/** Saved help, attempts and the limits reached in this sitting. */
export interface PracticeState {
  readonly help: Readonly<Record<string, ItemHelp>>;
  readonly attempts: readonly PracticeAttempt[];
  readonly extra: readonly number[];
  readonly exhausted: readonly number[];
}

/** The remaining questions and their practice history. */
export interface PracticeQueue {
  readonly queue: readonly number[];
  readonly missed: readonly number[];
  readonly practice?: PracticeState;
}

/** An exercise can append reserved items without putting them in its initial queue. */
export interface PracticePlan {
  readonly initialCount: number;
  readonly variants: readonly (readonly number[])[];
}

/** Begin a sitting with no help or answers recorded. */
export const emptyPractice = (): PracticeState => ({
  help: {},
  attempts: [],
  extra: [],
  exhausted: [],
});

/** Count the help already shown before recording an answer. */
export function helpAt(
  state: PracticeState | undefined,
  item: number,
): ItemHelp {
  return state?.help[String(item)] ?? { hints: 0, answer: false };
}

/** Revealing help leaves the current question in place. */
export function helped(
  state: PracticeState | undefined,
  item: number,
  help: ItemHelp,
): PracticeState {
  const current = state ?? emptyPractice();
  const before = helpAt(current, item);
  return {
    ...current,
    help: {
      ...current.help,
      [item]: {
        hints: Math.max(before.hints, help.hints),
        answer: before.answer || help.answer,
        ...(help.shown === undefined ? {} : { shown: help.shown }),
        ...listeningHelp(before, help),
      },
    },
  };
}

/** Give each original question at most one later attempt, using an unseen reserve when available. */
export function answeredPractice(
  state: PracticeQueue,
  passed: boolean,
  variants: readonly number[] = [],
  detail: { readonly answerId?: string; readonly status?: string } = {},
): PracticeQueue {
  const [item, ...rest] = state.queue;
  if (item === undefined) {
    return state;
  }
  const practice = state.practice ?? emptyPractice();
  const help = helpAt(practice, item);
  const tried =
    practice.attempts.some((attempt) => attempt.item === item) ||
    state.missed.includes(item);
  const extra = tried || practice.extra.includes(item);
  const next =
    !passed && !extra
      ? (variants.find(
          (at) =>
            !state.queue.includes(at) &&
            !practice.attempts.some((attempt) => attempt.item === at) &&
            !practice.extra.includes(at),
        ) ?? item)
      : undefined;
  const attempt: PracticeAttempt = {
    item,
    ...help,
    passed,
    ...detail,
    outcome: !passed
      ? "missed"
      : help.answer
        ? "answer"
        : help.audioFallback === true
          ? "text"
          : help.transcript === true
            ? "transcript"
            : help.hints > 0
              ? "hint"
              : tried
                ? "retry"
                : "first",
  };
  return {
    queue: next === undefined ? rest : [...rest, next],
    missed:
      passed || state.missed.includes(item)
        ? state.missed
        : [...state.missed, item],
    practice: {
      ...helped(practice, item, { ...help, answer: true, shown: false }),
      attempts: [...practice.attempts, attempt],
      extra: next === undefined ? practice.extra : [...practice.extra, next],
      exhausted:
        !passed && extra ? [...practice.exhausted, item] : practice.exhausted,
    },
  };
}

/** Stored help has a nonnegative clue count and explicit answer exposure. */
function validHelp(value: ItemHelp): boolean {
  return (
    value !== null &&
    typeof value === "object" &&
    Number.isInteger(value.hints) &&
    value.hints >= 0 &&
    typeof value.answer === "boolean" &&
    (value.shown === undefined || typeof value.shown === "boolean") &&
    ["listened", "transcript", "audioFallback"].every((key) => {
      const field = value[key as keyof ItemHelp];
      return field === undefined || typeof field === "boolean";
    })
  );
}

/** Read stored assistance without accepting malformed or out-of-range indexes. */
export function practiceState(
  value: unknown,
  count: number,
): PracticeState | undefined {
  if (value === null || typeof value !== "object") {
    return undefined;
  }
  const state = value as PracticeState;
  const index = (at: number): boolean =>
    Number.isInteger(at) && at >= 0 && at < count;
  if (
    state.help === null ||
    typeof state.help !== "object" ||
    Array.isArray(state.help) ||
    !Object.entries(state.help).every(
      ([at, value]) =>
        String(Number(at)) === at && index(Number(at)) && validHelp(value),
    ) ||
    !Array.isArray(state.attempts) ||
    state.attempts.length > count * 2 ||
    !state.attempts.every(
      (attempt) =>
        validHelp(attempt) &&
        index(attempt.item) &&
        typeof attempt.passed === "boolean" &&
        (attempt.answerId === undefined ||
          typeof attempt.answerId === "string") &&
        (attempt.status === undefined ||
          ["target", "valid-other", "error", "unverified"].includes(
            attempt.status,
          )) &&
        [
          "first",
          "hint",
          "answer",
          "retry",
          "missed",
          "transcript",
          "text",
        ].includes(attempt.outcome),
    ) ||
    ![state.extra, state.exhausted].every(
      (values) =>
        Array.isArray(values) &&
        new Set(values).size === values.length &&
        values.every(index),
    )
  ) {
    return undefined;
  }
  return state;
}

/** Assistance remains exposed across retries and concurrent reports. */
export function listeningHelp(
  before: ItemHelp,
  after: ItemHelp,
): Partial<ItemHelp> {
  return Object.fromEntries(
    (["listened", "transcript", "audioFallback"] as const).flatMap((key) =>
      before[key] === undefined && after[key] === undefined
        ? []
        : [[key, before[key] === true || after[key] === true]],
    ),
  );
}

/** Text exposure and playback failure exclude independent listening evidence. */
export function independentAnswer(attempt: ItemHelp): boolean {
  return (
    attempt.hints === 0 &&
    !attempt.answer &&
    attempt.transcript !== true &&
    attempt.audioFallback !== true
  );
}

/** Listening answers require completed playback or explicit text exposure. */
export function listeningReady(help: ItemHelp): boolean {
  return (
    help.listened === true ||
    help.transcript === true ||
    help.audioFallback === true ||
    help.answer
  );
}
